import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, adminToken, API, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { audio } from "@/lib/audio";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, Radio, BookText, Package, Download, LogOut, RefreshCw, X, Plus,
  Trash2, Power, Upload, Search, Users, DoorClosed, MessageSquare, Check,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "rooms", label: "Live Rooms", icon: Radio },
  { id: "prompts", label: "Prompts", icon: BookText },
  { id: "app", label: "App / Hub", icon: Package },
  { id: "downloads", label: "Downloads", icon: Download },
  { id: "feedback", label: "Feedback", icon: MessageSquare },
];

export default function AdminPanel() {
  const nav = useNavigate();
  const [ok, setOk] = useState(null);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!adminToken.get()) { nav("/admin/login"); return; }
    adminApi.get("/admin/me").then(() => setOk(true)).catch(() => { adminToken.clear(); nav("/admin/login"); });
  }, [nav]);

  const logout = () => { adminToken.clear(); audio.play("click"); nav("/admin/login"); };

  if (!ok) return <div className="min-h-screen flex items-center justify-center font-hand font-bold text-2xl">Loading Control Center…</div>;

  return (
    <div className="min-h-screen">
      <header className="border-b-2 border-ink/20" style={{ background: "#F4EBD0" }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="font-hand font-bold text-2xl">LOSTDC <span className="text-marker-blue">Control Center</span></div>
          <button data-testid="admin-logout" onClick={logout} className="btn-sketch px-3 py-1.5 text-sm"><LogOut size={14} strokeWidth={2.5} className="inline mr-1" />Sign out</button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex flex-wrap gap-2 mb-5">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => { setTab(t.id); audio.play("click"); }}
              className="font-hand font-bold px-4 py-2 inline-flex items-center gap-2" style={{ borderRadius: 10, border: "2px solid #1C1B1A", background: tab === t.id ? "#FFDD2D" : "#FDFBF7", boxShadow: tab === t.id ? "2px 2px 0 0 #1C1B1A" : "none" }}>
              <t.icon size={16} strokeWidth={2.5} />{t.label}
            </button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard />}
        {tab === "rooms" && <Rooms />}
        {tab === "prompts" && <Prompts />}
        {tab === "app" && <AppHub />}
        {tab === "downloads" && <Downloads />}
        {tab === "feedback" && <Feedback />}
      </div>
    </div>
  );
}

function Card({ label, value, color = "#FFE9A8" }) {
  return (
    <div className="sticky-note p-4" style={{ background: color }}>
      <div className="font-semibold text-sm text-ink2">{label}</div>
      <div className="font-hand font-bold text-3xl" style={{ fontFamily: "'Kalam',cursive" }}>{value}</div>
    </div>
  );
}

function ChartBox({ title, children }) {
  return (
    <div className="card-paper p-4">
      <div className="font-hand font-bold text-lg mb-2">{title}</div>
      <div style={{ height: 200 }}><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { adminApi.get("/admin/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, [load]);
  if (!d) return <div className="font-semibold">Loading…</div>;
  const c = d.cards, a = d.analytics;
  const colors = ["#FFE9A8", "#BAE3FB", "#FAC7D9", "#C7EFC2", "#FFCFA3", "#E9D5FF", "#FDE68A", "#BFDBFE"];
  const cardDefs = [
    ["Live Players", c.livePlayers], ["Active Rooms", c.activeRooms], ["Active Games", c.activeGames], ["Games Today", c.gamesToday],
    ["Total Games", c.totalGames], ["Total Players", c.totalPlayers], ["App Downloads", c.totalDownloads], ["Unique Emails", c.uniqueEmails],
  ];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cardDefs.map(([l, v], i) => <Card key={l} label={l} value={v} color={colors[i]} />)}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <ChartBox title="Games (14 days)">
          <LineChart data={d.charts.games}><CartesianGrid strokeDasharray="4 4" stroke="#00000015" /><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#2D7DD2" strokeWidth={3} dot={false} /></LineChart>
        </ChartBox>
        <ChartBox title="Room Creation (14 days)">
          <LineChart data={d.charts.rooms}><CartesianGrid strokeDasharray="4 4" stroke="#00000015" /><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#3E9E42" strokeWidth={3} dot={false} /></LineChart>
        </ChartBox>
        <ChartBox title="Downloads (14 days)">
          <BarChart data={d.charts.downloads}><CartesianGrid strokeDasharray="4 4" stroke="#00000015" /><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#E23B44" radius={[4, 4, 0, 0]} /></BarChart>
        </ChartBox>
      </div>
      <div className="card-paper p-4">
        <div className="font-hand font-bold text-lg mb-3">Game Analytics</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Completed Games", a.totalCompleted], ["Imposter Wins", a.imposterWins], ["Player Wins", a.playerWins], ["Misdirector Wins", a.misdirectorSuccess], ["Avg Rounds", a.avgRounds], ["Avg Players", a.avgPlayers], ["Avg Duration", `${a.avgDurationSec}s`]].map(([l, v]) => (
            <div key={l} className="card-flat p-3"><div className="text-sm font-semibold text-ink2">{l}</div><div className="font-hand font-bold text-2xl">{v}</div></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Rooms() {
  const [snap, setSnap] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => { adminApi.get("/admin/rooms").then((r) => setSnap(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, [load]);
  const openDetail = (code) => adminApi.get(`/admin/rooms/${code}`).then((r) => setDetail(r.data)).catch((e) => toast.error(errMsg(e)));
  const close = async (code) => { if (!window.confirm(`Close room ${code}?`)) return; await adminApi.post(`/admin/rooms/${code}/close`).catch((e) => toast.error(errMsg(e))); toast.success(`Room ${code} closed`); setDetail(null); load(); };
  if (!snap) return <div className="font-semibold">Loading…</div>;
  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4 max-w-md">
        <Card label="Live Players" value={snap.livePlayers} color="#BAE3FB" />
        <Card label="Active Rooms" value={snap.activeRooms} color="#C7EFC2" />
        <Card label="Active Games" value={snap.activeGames} color="#FAC7D9" />
      </div>
      <div className="card-paper p-3 overflow-x-auto">
        {snap.rooms.length === 0 ? <div className="font-semibold text-ink2 p-6 text-center">No active rooms right now.</div> : (
          <table className="w-full text-sm" data-testid="rooms-table">
            <thead><tr className="text-left font-hand font-bold border-b-2 border-ink/20">
              {["Code", "Host", "Players", "Round", "Phase", "Difficulty", ""].map((h) => <th key={h} className="p-2">{h}</th>)}
            </tr></thead>
            <tbody>
              {snap.rooms.map((r) => (
                <tr key={r.code} className="border-b border-ink/10 hover:bg-cream">
                  <td className="p-2 font-hand font-bold" style={{ fontFamily: "'JetBrains Mono',monospace" }}>{r.code}</td>
                  <td className="p-2 font-semibold">{r.host}</td>
                  <td className="p-2">{r.connected}/{r.players}</td>
                  <td className="p-2">{r.round}/{r.totalRounds}</td>
                  <td className="p-2"><span className="sticky-note bg-sticky-yellow px-2 py-0.5 text-xs font-bold">{r.phase}</span></td>
                  <td className="p-2 font-semibold">{r.difficulty}</td>
                  <td className="p-2 flex gap-1">
                    <button onClick={() => openDetail(r.code)} className="btn-sketch px-2 py-1 text-xs">View</button>
                    <button onClick={() => close(r.code)} className="btn-sketch px-2 py-1 text-xs" style={{ background: "#FAC7D9" }}>Close</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(28,27,26,0.45)" }} onClick={() => setDetail(null)}>
          <div className="card-paper p-5 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3"><div className="font-hand font-bold text-2xl">Room {detail.code}</div><button onClick={() => setDetail(null)} className="btn-sketch p-1.5"><X size={16} strokeWidth={2.5} /></button></div>
            <div className="font-semibold text-ink2 text-sm mb-3">Phase: {detail.phase} · Round {detail.round}/{detail.totalRounds} · {detail.settings.difficulty}</div>
            <div className="space-y-2">
              {detail.players.map((p) => (
                <div key={p.id} className="card-flat p-2 flex items-center justify-between">
                  <span className="font-semibold flex items-center gap-2"><Users size={14} />{p.name} {!p.alive && "💀"} {!p.connected && "(offline)"}</span>
                  <span className="text-sm">{p.role || "—"} · <b>{p.score}</b></span>
                </div>
              ))}
            </div>
            <button onClick={() => close(detail.code)} className="btn-sketch w-full mt-4 py-2" style={{ background: "#FAC7D9" }}><DoorClosed size={16} strokeWidth={2.5} className="inline mr-1" />Force Close Room</button>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_PROMPT = { normal: "", imposter: "", category: "Objects", difficulty: "Normal", similarity: 75, active: true };
function Prompts() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState({ search: "", category: "", difficulty: "", status: "", page: 1 });
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    adminApi.get("/admin/prompts", { params: q }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (edit.id) await adminApi.put(`/admin/prompts/${edit.id}`, edit);
      else await adminApi.post("/admin/prompts", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (p) => { await adminApi.post(`/admin/prompts/${p.id}/toggle`); load(); };
  const del = async (p) => { if (!window.confirm("Delete this prompt pair?")) return; await adminApi.delete(`/admin/prompts/${p.id}`); toast.success("Deleted"); load(); };
  if (!data) return <div className="font-semibold">Loading…</div>;
  const pages = Math.ceil(data.total / data.limit) || 1;
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <div className="card-flat flex items-center px-2"><Search size={16} /><input data-testid="prompt-search" value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value, page: 1 })} placeholder="Search prompts…" className="px-2 py-2 bg-transparent outline-none font-semibold" /></div>
        <select value={q.category} onChange={(e) => setQ({ ...q, category: e.target.value, page: 1 })} className="card-flat px-2 py-2 font-semibold"><option value="">All categories</option>{data.categories.map((c) => <option key={c}>{c}</option>)}</select>
        <select value={q.difficulty} onChange={(e) => setQ({ ...q, difficulty: e.target.value, page: 1 })} className="card-flat px-2 py-2 font-semibold"><option value="">All difficulty</option>{["Easy", "Normal", "Hard"].map((c) => <option key={c}>{c}</option>)}</select>
        <select value={q.status} onChange={(e) => setQ({ ...q, status: e.target.value, page: 1 })} className="card-flat px-2 py-2 font-semibold"><option value="">All status</option><option value="active">Active</option><option value="disabled">Disabled</option></select>
        <button data-testid="prompt-add" onClick={() => setEdit({ ...EMPTY_PROMPT })} className="btn-sketch px-3 py-2" style={{ background: "#C7EFC2" }}><Plus size={16} strokeWidth={2.5} className="inline mr-1" />Add</button>
        <span className="font-semibold text-ink2 ml-auto">{data.total} pairs</span>
      </div>
      <div className="card-paper p-3 overflow-x-auto">
        <table className="w-full text-sm" data-testid="prompts-table">
          <thead><tr className="text-left font-hand font-bold border-b-2 border-ink/20">{["Normal", "Imposter", "Category", "Diff", "Sim", "Use", "Status", ""].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} className="border-b border-ink/10">
                <td className="p-2 font-semibold">{p.normal}</td>
                <td className="p-2 font-semibold">{p.imposter}</td>
                <td className="p-2">{p.category}</td>
                <td className="p-2">{p.difficulty}</td>
                <td className="p-2">{p.similarity}%</td>
                <td className="p-2">{p.usage}</td>
                <td className="p-2"><span className="px-2 py-0.5 text-xs font-bold" style={{ borderRadius: 6, background: p.active ? "#C7EFC2" : "#F0D0D0" }}>{p.active ? "active" : "off"}</span></td>
                <td className="p-2 flex gap-1">
                  <button onClick={() => setEdit(p)} className="btn-sketch px-2 py-1 text-xs">Edit</button>
                  <button onClick={() => toggle(p)} className="btn-sketch px-1.5 py-1 text-xs"><Power size={13} strokeWidth={2.5} /></button>
                  <button onClick={() => del(p)} className="btn-sketch px-1.5 py-1 text-xs" style={{ background: "#FAC7D9" }}><Trash2 size={13} strokeWidth={2.5} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 mt-3 justify-center">
        <button disabled={q.page <= 1} onClick={() => setQ({ ...q, page: q.page - 1 })} className="btn-sketch px-3 py-1.5 text-sm">Prev</button>
        <span className="font-hand font-bold">{q.page} / {pages}</span>
        <button disabled={q.page >= pages} onClick={() => setQ({ ...q, page: q.page + 1 })} className="btn-sketch px-3 py-1.5 text-sm">Next</button>
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(28,27,26,0.45)" }} onClick={() => setEdit(null)}>
          <div className="card-paper p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="prompt-modal">
            <div className="font-hand font-bold text-2xl mb-3">{edit.id ? "Edit" : "New"} Prompt Pair</div>
            {[["normal", "Normal Prompt"], ["imposter", "Imposter Prompt"]].map(([k, l]) => (
              <div key={k} className="mb-2"><label className="font-semibold text-sm">{l}</label><input value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} className="w-full card-flat px-3 py-2 font-semibold outline-none" data-testid={`prompt-${k}`} /></div>
            ))}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div><label className="font-semibold text-sm">Category</label><input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="w-full card-flat px-2 py-2 font-semibold outline-none" /></div>
              <div><label className="font-semibold text-sm">Difficulty</label><select value={edit.difficulty} onChange={(e) => setEdit({ ...edit, difficulty: e.target.value })} className="w-full card-flat px-2 py-2 font-semibold"><option>Easy</option><option>Normal</option><option>Hard</option></select></div>
              <div><label className="font-semibold text-sm">Similarity</label><input type="number" value={edit.similarity} onChange={(e) => setEdit({ ...edit, similarity: parseInt(e.target.value) || 0 })} className="w-full card-flat px-2 py-2 font-semibold outline-none" /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEdit(null)} className="btn-sketch px-4 py-2">Cancel</button>
              <button data-testid="prompt-save" onClick={save} className="btn-sketch px-4 py-2" style={{ background: "#C7EFC2" }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AppHub() {
  const [hub, setHub] = useState(null);
  const [form, setForm] = useState({ name: "LOSTDC Imposter Draw", version: "1.0.0", platform: "Android", description: "", releaseNotes: "", downloadLabel: "DOWNLOAD APP" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [site, setSite] = useState({ about: "", features: [], links: {}, requireGmail: true });
  const load = useCallback(() => { adminApi.get("/admin/hub").then((r) => { setHub(r.data); setSite({ about: r.data.site.about || "", features: r.data.site.features || [], links: r.data.site.links || {}, requireGmail: r.data.site.requireGmail !== false }); }).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choose a file to upload."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await adminApi.post("/admin/hub/app", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Release published!"); setFile(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const saveSite = async () => { await adminApi.put("/admin/hub/site", site).catch((e) => toast.error(errMsg(e))); toast.success("Hub content saved"); };
  const activate = async (id) => { await adminApi.post(`/admin/hub/app/${id}/activate`); toast.success("Set as current release"); load(); };
  if (!hub) return <div className="font-semibold">Loading…</div>;

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="card-paper p-5">
        <div className="font-hand font-bold text-2xl mb-3"><Upload size={20} strokeWidth={2.5} className="inline mr-1" />Publish New Release</div>
        {[["name", "App Name"], ["version", "Version"], ["platform", "Platform"], ["downloadLabel", "Button Label"]].map(([k, l]) => (
          <div key={k} className="mb-2"><label className="font-semibold text-sm">{l}</label><input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full card-flat px-3 py-2 font-semibold outline-none" data-testid={`app-${k}`} /></div>
        ))}
        <div className="mb-2"><label className="font-semibold text-sm">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full card-flat px-3 py-2 font-semibold outline-none" rows={2} /></div>
        <div className="mb-2"><label className="font-semibold text-sm">Release Notes</label><textarea value={form.releaseNotes} onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })} className="w-full card-flat px-3 py-2 font-semibold outline-none" rows={3} /></div>
        <div className="mb-3"><label className="font-semibold text-sm">App File (APK/AAB/ZIP/etc.)</label><input type="file" data-testid="app-file" onChange={(e) => setFile(e.target.files[0])} className="w-full card-flat px-3 py-2 font-semibold" /></div>
        <button data-testid="app-upload" onClick={upload} disabled={busy} className="btn-sketch w-full py-2" style={{ background: "#C7EFC2" }}>{busy ? "Uploading…" : "PUBLISH RELEASE"}</button>

        <div className="font-hand font-bold text-xl mt-6 mb-2">Releases</div>
        <div className="space-y-2">
          {hub.releases.length === 0 && <div className="font-semibold text-ink2">No releases yet.</div>}
          {hub.releases.map((r) => (
            <div key={r.id} className="card-flat p-3 flex items-center justify-between">
              <div><div className="font-hand font-bold">{r.name} <span className="text-sm">v{r.version}</span></div><div className="text-xs font-semibold text-ink2">{r.platform} · {r.sizeLabel} · {new Date(r.createdAt).toLocaleDateString()}</div></div>
              {r.active ? <span className="sticky-note bg-sticky-green px-2 py-1 text-xs font-bold">CURRENT</span> : <button onClick={() => activate(r.id)} className="btn-sketch px-2 py-1 text-xs">Make current</button>}
            </div>
          ))}
        </div>
      </div>

      <div className="card-paper p-5">
        <div className="font-hand font-bold text-2xl mb-3">Hub Content</div>
        <label className="font-semibold text-sm">About LOSTDC</label>
        <textarea value={site.about} onChange={(e) => setSite({ ...site, about: e.target.value })} className="w-full card-flat px-3 py-2 font-semibold outline-none mb-3" rows={3} data-testid="site-about" />
        <div className="font-hand font-bold mb-1">Features</div>
        {site.features.map((f, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={f.title} onChange={(e) => { const nf = [...site.features]; nf[i] = { ...f, title: e.target.value }; setSite({ ...site, features: nf }); }} placeholder="Title" className="card-flat px-2 py-1.5 font-semibold w-1/3 outline-none" />
            <input value={f.desc} onChange={(e) => { const nf = [...site.features]; nf[i] = { ...f, desc: e.target.value }; setSite({ ...site, features: nf }); }} placeholder="Description" className="card-flat px-2 py-1.5 font-semibold flex-1 outline-none" />
            <button onClick={() => setSite({ ...site, features: site.features.filter((_, j) => j !== i) })} className="btn-sketch px-2" style={{ background: "#FAC7D9" }}><Trash2 size={13} strokeWidth={2.5} /></button>
          </div>
        ))}
        <button onClick={() => setSite({ ...site, features: [...site.features, { title: "", desc: "" }] })} className="btn-sketch px-3 py-1.5 text-sm mb-3"><Plus size={13} strokeWidth={2.5} className="inline mr-1" />Add feature</button>
        <div className="font-hand font-bold mb-1">Links</div>
        {["website", "discord", "twitter", "support"].map((k) => (
          <div key={k} className="mb-2"><input value={site.links[k] || ""} onChange={(e) => setSite({ ...site, links: { ...site.links, [k]: e.target.value } })} placeholder={`${k} URL`} className="w-full card-flat px-3 py-1.5 font-semibold outline-none" /></div>
        ))}
        <label className="flex items-center gap-2 font-semibold text-sm my-2 cursor-pointer"><input type="checkbox" checked={site.requireGmail} onChange={(e) => setSite({ ...site, requireGmail: e.target.checked })} />Require Gmail address for downloads</label>
        <button data-testid="site-save" onClick={saveSite} className="btn-sketch w-full py-2" style={{ background: "#FFDD2D" }}>Save Hub Content</button>
      </div>
    </div>
  );
}

function Downloads() {
  const [d, setD] = useState(null);
  const [range, setRange] = useState("30d");
  useEffect(() => { adminApi.get("/admin/downloads", { params: { range } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [range]);
  if (!d) return <div className="font-semibold">Loading…</div>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card label="Total Downloads" value={d.total} color="#FFE9A8" />
        <Card label="Unique Emails" value={d.unique} color="#BAE3FB" />
        <Card label="Opted-in" value={d.optedIn ?? 0} color="#E9D5FF" />
        <Card label="Today" value={d.today} color="#C7EFC2" />
        <Card label="This Week" value={d.week} color="#FAC7D9" />
        <Card label="This Month" value={d.month} color="#FFCFA3" />
      </div>
      <div className="flex gap-2">{["7d", "30d", "all"].map((r) => <button key={r} onClick={() => setRange(r)} className="btn-sketch px-3 py-1.5 text-sm" style={{ background: range === r ? "#FFDD2D" : "#FDFBF7" }}>{r}</button>)}</div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 card-paper p-4"><div className="font-hand font-bold text-lg mb-2">Downloads per day</div><div style={{ height: 200 }}><ResponsiveContainer><BarChart data={d.series}><CartesianGrid strokeDasharray="4 4" stroke="#00000015" /><XAxis dataKey="day" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#2D7DD2" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div></div>
        <div className="card-paper p-4"><div className="font-hand font-bold text-lg mb-2">By Version</div>{d.versions.map((v) => <div key={v.name} className="flex justify-between font-semibold border-b border-ink/10 py-1"><span>{v.name}</span><b>{v.value}</b></div>)}{!d.versions.length && <div className="font-semibold text-ink2">No data</div>}</div>
        <div className="card-paper p-4"><div className="font-hand font-bold text-lg mb-2">By Platform</div>{d.platforms.map((v) => <div key={v.name} className="flex justify-between font-semibold border-b border-ink/10 py-1"><span>{v.name}</span><b>{v.value}</b></div>)}{!d.platforms.length && <div className="font-semibold text-ink2">No data</div>}</div>
      </div>
      <div className="card-paper p-4">
        <div className="font-hand font-bold text-lg mb-2">Latest Downloads</div>
        {d.latest.length === 0 ? <div className="font-semibold text-ink2">No downloads yet.</div> : (
          <table className="w-full text-sm"><thead><tr className="text-left font-hand font-bold border-b-2 border-ink/20"><th className="p-2">Email</th><th className="p-2">Updates opt-in</th><th className="p-2">Version</th><th className="p-2">Platform</th><th className="p-2">When</th></tr></thead>
            <tbody>{d.latest.map((l, i) => <tr key={i} className="border-b border-ink/10"><td className="p-2 font-semibold" style={{ fontFamily: "'JetBrains Mono',monospace" }}>{l.email}</td><td className="p-2">{l.optIn ? <span className="px-2 py-0.5 text-xs font-bold" style={{ borderRadius: 6, background: "#C7EFC2" }}>✓ opted in</span> : <span className="px-2 py-0.5 text-xs font-bold text-ink2" style={{ borderRadius: 6, background: "#EADFBF" }}>— no</span>}</td><td className="p-2">{l.version}</td><td className="p-2">{l.platform}</td><td className="p-2 text-ink2">{new Date(l.ts).toLocaleString()}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Feedback() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { adminApi.get("/admin/feedback").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const markRead = async (id) => { await adminApi.post(`/admin/feedback/${id}/read`); load(); };
  const del = async (id) => { if (!window.confirm("Delete this feedback?")) return; await adminApi.delete(`/admin/feedback/${id}`); toast.success("Deleted"); load(); };
  if (!d) return <div className="font-semibold">Loading…</div>;
  return (
    <div>
      <div className="flex gap-3 mb-4 max-w-sm">
        <Card label="Total Feedback" value={d.total} color="#FFE9A8" />
        <Card label="Unread" value={d.unread} color="#FAC7D9" />
      </div>
      {d.items.length === 0 ? <div className="card-paper p-6 font-semibold text-ink2 text-center">No feedback yet. When players submit feedback from the Creators Hub, it appears here.</div> : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="feedback-list">
          {d.items.map((fb) => (
            <div key={fb.id} className="card-paper p-4" style={{ background: fb.read ? "#FDFBF7" : "#FFF7D6" }}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-hand font-bold text-lg">{fb.username} {!fb.read && <span className="text-xs align-middle px-1.5 py-0.5" style={{ borderRadius: 6, background: "#FAC7D9" }}>NEW</span>}</div>
                  <div className="text-xs font-bold text-marker-blue" style={{ fontFamily: "'JetBrains Mono',monospace" }}>🔒 {fb.email}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {!fb.read && <button onClick={() => markRead(fb.id)} className="btn-sketch px-1.5 py-1 text-xs" title="Mark read"><Check size={13} strokeWidth={2.5} /></button>}
                  <button onClick={() => del(fb.id)} className="btn-sketch px-1.5 py-1 text-xs" style={{ background: "#FAC7D9" }}><Trash2 size={13} strokeWidth={2.5} /></button>
                </div>
              </div>
              <p className="font-semibold text-ink2 mt-2 whitespace-pre-line break-words">{fb.message}</p>
              <div className="text-xs font-semibold text-ink2 mt-2">{new Date(fb.ts).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
